import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiMapPin, FiAward } = FiIcons;

const ResultsSummary = ({ results }) => {
  const topMatches = results.matches?.slice(0, 3) || [];
  const totalSavings = topMatches.reduce((sum, match) => sum + (match.estimated_savings || 0), 0);
  const bestFit = results.matches?.[0];

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg border border-gray-200 p-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Potential Savings */}
        <div className="text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start mb-2">
            <div className="w-10 h-10 bg-success-100 rounded-full flex items-center justify-center mr-3">
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-success-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Potential Savings
            </h3>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900">
            {formatCurrency(totalSavings)}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Top 3 incentives combined
          </p>
        </div>

        {/* Best Fit */}
        {bestFit && (
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start mb-2">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                <SafeIcon icon={FiAward} className="w-5 h-5 text-primary-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Best Fit
              </h3>
            </div>
            <p className="text-xl font-bold text-gray-900">
              {bestFit.jurisdiction_name}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {bestFit.incentive_type} • {bestFit.mark_stars}/5 stars
            </p>
          </div>
        )}

        {/* Number of Matches */}
        <div className="text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start mb-2">
            <div className="w-10 h-10 bg-warning-100 rounded-full flex items-center justify-center mr-3">
              <SafeIcon icon={FiMapPin} className="w-5 h-5 text-warning-600" />
            </div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Total Matches
            </h3>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900">
            {results.matches?.length || 0}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Eligible locations found
          </p>
        </div>
      </div>

      {/* Summary Text */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-700">
          <strong>Summary:</strong> Up to {formatCurrency(totalSavings)} potential savings 
          across {results.matches?.length || 0} locations. 
          {bestFit && (
            <> Best fit: <strong>{bestFit.jurisdiction_name}</strong></>
          )}
        </p>
      </div>
    </motion.div>
  );
};

export default ResultsSummary;